/**
 * Elemental Death Effect Renderer (L3-ELEMENT-DEATH)
 *
 * Renders element-specific death effects when creatures die.
 * Fire: Burn to ash with ascending particles
 * Ice: Shatter into angular crystals
 * Lightning: Electrocution arcs
 * Poison: Dissolving drips
 * Physical: Standard gray smoke (unchanged)
 */

import * as PIXI from 'pixi.js';
import { getElementConfig, type ElementType } from './element-effect-configs.js';

/**
 * Death effect configuration per element
 */
interface ElementDeathConfig {
  particleCount: number;
  duration: number;  // ms
  behavior: 'rise' | 'shatter' | 'arc' | 'drip' | 'disperse';
  colors: number[];
}

const ELEMENT_DEATH_CONFIGS: Record<string, ElementDeathConfig> = {
  fire: {
    particleCount: 10,
    duration: 500,
    behavior: 'rise',
    colors: [0xFF4500, 0xFF8C00, 0x1a1a1a]  // Red -> Orange -> Black
  },
  ice: {
    particleCount: 12,
    duration: 400,
    behavior: 'shatter',
    colors: [0x00FFFF, 0xFFFFFF, 0x87CEEB]  // Cyan -> White -> Light blue
  },
  lightning: {
    particleCount: 8,
    duration: 300,
    behavior: 'arc',
    colors: [0xFFFF00, 0x0099FF, 0xFFFFFF]  // Yellow -> Blue -> White
  },
  poison: {
    particleCount: 10,
    duration: 600,
    behavior: 'drip',
    colors: [0x00FF00, 0x32CD32, 0x228B22]  // Neon green -> Lime -> Forest
  },
  physical: {
    particleCount: 5,
    duration: 300,
    behavior: 'disperse',
    colors: [0xCCCCCC, 0x888888, 0x444444]  // Gray shades
  }
};

/**
 * Get death config for an element, defaulting to physical
 */
function getDeathConfig(element: string): ElementDeathConfig {
  return ELEMENT_DEATH_CONFIGS[element] || ELEMENT_DEATH_CONFIGS.physical;
}

/**
 * Main class for rendering elemental death effects
 */
export class ElementalDeathEffectRenderer {
  private container: PIXI.Container;

  constructor(container: PIXI.Container) {
    this.container = container;
  }

  /**
   * Create death effect at position based on element
   */
  public createDeathEffect(
    position: { x: number; y: number },
    element: string
  ): void {
    switch (element) {
      case 'fire':
        this.createFireDeath(position);
        break;
      case 'ice':
        this.createIceDeath(position);
        break;
      case 'lightning':
        this.createLightningDeath(position);
        break;
      case 'poison':
        this.createPoisonDeath(position);
        break;
      default:
        this.createDefaultDeath(position);
    }
  }

  /**
   * Fire death: Burn to ash with rising particles
   */
  private createFireDeath(position: { x: number; y: number }): void {
    const config = getDeathConfig('fire');

    for (let i = 0; i < config.particleCount; i++) {
      const particle = new PIXI.Graphics();
      const size = 4 + Math.random() * 4;

      // Pick initial color
      particle.circle(0, 0, size);
      particle.fill({ color: config.colors[0] });

      particle.position.set(
        position.x + (Math.random() - 0.5) * 30,
        position.y + (Math.random() - 0.5) * 20
      );
      particle.blendMode = 'add' as PIXI.BLEND_MODES;
      this.container.addChild(particle);

      // Animate
      const startY = particle.y;
      const delay = i * 30;  // Stagger
      let frame = 0;
      const totalFrames = 30;

      setTimeout(() => {
        const animate = () => {
          frame++;
          const progress = frame / totalFrames;

          // Rise upward
          particle.y = startY - progress * 50;

          // Fade and expand
          particle.alpha = 1 - progress;
          particle.scale.set(1 + progress * 0.5);

          // Color shift to black
          if (progress > 0.6) {
            particle.tint = config.colors[2];  // Black
          } else if (progress > 0.3) {
            particle.tint = config.colors[1];  // Orange
          }

          if (frame < totalFrames) {
            requestAnimationFrame(animate);
          } else {
            this.container.removeChild(particle);
            particle.destroy();
          }
        };
        animate();
      }, delay);
    }
  }

  /**
   * Ice death: Shatter into angular crystals
   */
  private createIceDeath(position: { x: number; y: number }): void {
    const config = getDeathConfig('ice');

    for (let i = 0; i < config.particleCount; i++) {
      const particle = new PIXI.Graphics();

      // Draw angular shard using poly()
      const size = 5 + Math.random() * 5;
      particle.poly([
        0, -size,
        size * 0.6, 0,
        0, size * 0.7,
        -size * 0.6, 0
      ]);
      particle.fill({ color: config.colors[Math.floor(Math.random() * config.colors.length)] });

      particle.position.set(position.x, position.y);
      particle.blendMode = 'add' as PIXI.BLEND_MODES;
      this.container.addChild(particle);

      // Random outward direction
      const angle = (i / config.particleCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const speed = 2 + Math.random() * 3;
      const rotationSpeed = (Math.random() - 0.5) * 0.3;

      let frame = 0;
      const totalFrames = 24;

      const animate = () => {
        frame++;
        const progress = frame / totalFrames;

        // Explode outward
        particle.x += Math.cos(angle) * speed * (1 - progress);
        particle.y += Math.sin(angle) * speed * (1 - progress) + progress * 2;  // Slight fall

        // Rotate
        particle.rotation += rotationSpeed;

        // Fade
        particle.alpha = 1 - progress;

        if (frame < totalFrames) {
          requestAnimationFrame(animate);
        } else {
          this.container.removeChild(particle);
          particle.destroy();
        }
      };

      animate();
    }
  }

  /**
   * Lightning death: Electrical arcs
   */
  private createLightningDeath(position: { x: number; y: number }): void {
    const config = getDeathConfig('lightning');

    for (let i = 0; i < config.particleCount; i++) {
      const particle = new PIXI.Graphics();

      // Draw small arc/line
      const length = 10 + Math.random() * 15;
      const angle = Math.random() * Math.PI * 2;

      particle.moveTo(0, 0);
      particle.lineTo(
        Math.cos(angle) * length,
        Math.sin(angle) * length
      );
      particle.stroke({ width: 2, color: config.colors[0] });

      particle.position.set(
        position.x + (Math.random() - 0.5) * 20,
        position.y + (Math.random() - 0.5) * 20
      );
      particle.blendMode = 'add' as PIXI.BLEND_MODES;
      this.container.addChild(particle);

      // Rapid pulse animation
      let frame = 0;
      const totalFrames = 18;
      const pulseSpeed = 0.5;

      const animate = () => {
        frame++;
        const progress = frame / totalFrames;

        // Rapid pulse
        const pulse = Math.sin(frame * pulseSpeed * Math.PI) * 0.5 + 0.5;
        particle.alpha = pulse * (1 - progress);

        // Slight random jitter
        particle.x += (Math.random() - 0.5) * 2;
        particle.y += (Math.random() - 0.5) * 2;

        if (frame < totalFrames) {
          requestAnimationFrame(animate);
        } else {
          this.container.removeChild(particle);
          particle.destroy();
        }
      };

      animate();
    }
  }

  /**
   * Poison death: Dissolving drips
   */
  private createPoisonDeath(position: { x: number; y: number }): void {
    const config = getDeathConfig('poison');

    for (let i = 0; i < config.particleCount; i++) {
      const particle = new PIXI.Graphics();

      // Irregular blob shape
      const size = 3 + Math.random() * 5;
      particle.ellipse(0, 0, size, size * (1 + Math.random() * 0.5));
      particle.fill({ color: config.colors[Math.floor(Math.random() * config.colors.length)] });

      particle.position.set(
        position.x + (Math.random() - 0.5) * 25,
        position.y + (Math.random() - 0.5) * 15
      );
      particle.blendMode = 'add' as PIXI.BLEND_MODES;
      this.container.addChild(particle);

      const delay = i * 40;
      let frame = 0;
      const totalFrames = 36;

      setTimeout(() => {
        const animate = () => {
          frame++;
          const progress = frame / totalFrames;

          // Drip downward
          particle.y += 1.5 + progress * 2;

          // Stretch vertically
          particle.scale.y = 1 + progress * 0.5;
          particle.scale.x = 1 - progress * 0.3;

          // Fade
          particle.alpha = 1 - progress * 0.8;

          if (frame < totalFrames) {
            requestAnimationFrame(animate);
          } else {
            this.container.removeChild(particle);
            particle.destroy();
          }
        };
        animate();
      }, delay);
    }
  }

  /**
   * Default death: Gray smoke dispersion
   */
  private createDefaultDeath(position: { x: number; y: number }): void {
    const config = getDeathConfig('physical');

    for (let i = 0; i < config.particleCount; i++) {
      const particle = new PIXI.Graphics();
      const size = 5 + Math.random() * 5;

      particle.circle(0, 0, size);
      particle.fill({ color: config.colors[Math.floor(Math.random() * config.colors.length)] });

      particle.position.set(
        position.x + (Math.random() - 0.5) * 20,
        position.y
      );
      this.container.addChild(particle);

      // Random direction with upward bias
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.8;
      const speed = 1 + Math.random() * 1.5;

      let frame = 0;
      const totalFrames = 18;

      const animate = () => {
        frame++;
        const progress = frame / totalFrames;

        // Rise and disperse
        particle.x += Math.cos(angle) * speed;
        particle.y += Math.sin(angle) * speed;

        // Expand and fade
        particle.scale.set(1 + progress * 0.8);
        particle.alpha = 1 - progress;

        if (frame < totalFrames) {
          requestAnimationFrame(animate);
        } else {
          this.container.removeChild(particle);
          particle.destroy();
        }
      };

      animate();
    }
  }
}
